// WebSocket message types that match the backend implementation
export type MessageType =
  | "chat_message"
  | "chat_ooc"
  | "chat_ic"
  | "chat_whisper"
  | "chat_system"
  | "dice_roll"
  | "initiative_roll"
  | "skill_check"
  | "asset_shared"
  | "asset_updated"
  | "asset_share"
  | "asset_update"
  | "session_control"
  | "initiative_update"
  | "initiative_add"
  | "initiative_remove"
  | "turn_change"
  | "combat_state_change"
  | "health_update"
  | "status_effect"
  | "user_joined"
  | "user_left"
  | "session_state"
  | "system_alert"
  | "error"
  | "connect"
  | "disconnect"
  | "ping"
  | "pong"
  | "rtc_signal"
  | "voice_state_change"
  | "participants_list"
  | "music_control"
  | "notes_updated"
  | "notes_lock_updated"
  | "knowledge_updated"
  | "campaign_invite";

export interface WebSocketMessage {
  id: string;
  type: MessageType;
  sessionId: string;
  senderId: string;
  timestamp: string;
  data: unknown;
  priority?: number;
  recipients?: string[];
}

export interface ChatMessageData {
  message: string;
  messageType: string; // OOC, IC, Whisper, System
  characterName?: string;
  recipientId?: string; // For whispers
}

export interface DiceRollData {
  rollId: string;
  formula: string;
  results: number[];
  total: number;
  rollName?: string;
  characterId?: string;
  characterName?: string;
  isPrivate: boolean;
  isCritical?: boolean;
}

export interface AssetSharedData {
  assetId: string;
  assetName: string;
  assetType: string;
  sharedBy: string;
  permissions: string[];
  previewData?: unknown;
}

export interface SessionStateData {
  sessionId: string;
  state: string; // active, paused, ended
  participants: string[];
  currentTurn?: string;
  dmUserId: string;
}

export interface SystemNotificationData {
  notificationType: string;
  message: string;
  userId?: string;
  username?: string;
  characterId?: string;
  characterName?: string;
  isDM?: boolean;
  severity?: "info" | "warning" | "error";
  additionalData?: {
    sessionId?: string;
    dmUserId?: string;
    dmUsername?: string;
    sessionName?: string;
    timestamp?: number;
    [key: string]: unknown;
  };
}

// Connected participant info delivered via participants_list and user_joined WS events
export interface ParticipantInfo {
  userId: string;
  username: string;
  characterId?: string;
  characterName?: string;
  isDM: boolean;
}

export interface ParticipantsListData {
  sessionId: string;
  participants: ParticipantInfo[];
}

export interface ConnectionData {
  userId: string;
  username: string;
  characterName?: string;
  role: string; // DM, Player, Observer
}

export interface ErrorData {
  code: string;
  message: string;
}

export interface ConnectionInfo {
  id: string;
  userId: string;
  username: string;
  sessionId: string;
  role: string;
  isAuthenticated: boolean;
  connectedAt: string;
  lastActivity: string;
}

/* eslint-disable no-unused-vars */
export enum WebSocketState {
  CONNECTING = "CONNECTING",
  CONNECTED = "CONNECTED",
  DISCONNECTED = "DISCONNECTED",
  ERROR = "ERROR",
}
/* eslint-enable no-unused-vars */

export interface WebSocketConfig {
  url: string;
  userId: string;
  username?: string;
  sessionId?: string;
  characterId?: string;
  token?: string;
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectInterval?: number;
  heartbeatInterval?: number;
}

export interface WebSocketEventHandlers {
  onConnect?: () => void;
  onDisconnect?: (_code: number, _reason: string) => void;
  onError?: (_error: Event) => void;
  onMessage?: (_message: WebSocketMessage) => void;
  onStateChange?: (_state: WebSocketState) => void;
}

// Initiative and Combat Types

export interface InitiativeEntry {
  id: string;
  name: string;
  initiative: number;
  isPlayer: boolean;
  isActive: boolean;
  hp?: number;
  maxHp?: number;
  conditions?: string[];
}

export interface InitiativeUpdateData {
  entries: InitiativeEntry[];
  currentTurn: number;
  round: number;
  isTracking: boolean;
}

export interface InitiativeAddData {
  entry: InitiativeEntry;
}

export interface InitiativeRemoveData {
  entryId: string;
}

export interface TurnChangeData {
  currentTurn: number;
  entryId: string;
  entryName: string;
  round: number;
}

export interface CombatStateChangeData {
  state: string; // inactive, active, paused
  startedAt?: string;
  pausedAt?: string;
  endedAt?: string;
}

export interface HealthUpdateData {
  entryId: string;
  hp: number;
  maxHp: number;
  change: number;
  changeType: string; // damage, healing
}

export interface StatusEffectData {
  entryId: string;
  conditions: string[];
  added?: string[];
  removed?: string[];
}

export interface SessionControlData {
  action: string; // start, pause, resume, end
  targetId?: string;
  data?: string;
}

// Audio/Voice Communication Data Types
export interface RTCSignalData {
  type: "offer" | "answer" | "ice-candidate";
  targetUserId: string;
  sourceUserId: string;
  sessionId: string;
  sdp?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
}

export interface VoiceStateChangeData {
  userId: string;
  isMuted: boolean;
  isSpeaking: boolean;
  isEnabled?: boolean; // true = joined voice channel, false = left voice channel
}

// Music sharing — DM broadcasts ambient music to all session participants
export interface MusicControlData {
  /** play | pause | stop | volume | seek */
  action: "play" | "pause" | "stop" | "volume" | "seek";
  /** Direct audio file URL (MP3, OGG, WAV, etc.) */
  trackUrl?: string;
  trackTitle?: string;
  /** Playback position in seconds */
  position?: number;
  /** Volume level 0–1 */
  volume?: number;
}

// Collaborative notes — broadcast when the shared session notes are updated
export interface NotesUpdatedData {
  content: string;
  updatedBy: string;
  updatedAt: string;
  isPersonal: boolean;
}

// Knowledge panel — sent to a specific user when an asset or section is shared with their character
export interface KnowledgeUpdatedData {
  campaignId: number;
  characterId: number;
  assetId: number;
  assetName: string;
  sectionId?: number;
  sectionName?: string;
  sharedByName: string;
  action: "granted" | "revoked";
}

export interface NotesLockUpdatedData {
  sessionId: string;
  isLocked: boolean;
}
